import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { FileText, AlertTriangle, Star, Scale, Shield, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

// Animated Section Component
const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={fadeInUp}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Terms Section Component
const TermsSection = ({ icon: Icon, title, children, testId }) => {
  return (
    <AnimatedSection className="mb-12">
      <div className="flex items-start gap-4" data-testid={testId}>
        <div className="flex-shrink-0 w-12 h-12 bg-purple-900/30 flex items-center justify-center">
          <Icon className="w-6 h-6 text-purple-400" />
        </div>
        <div>
          <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-white mb-4">
            {title}
          </h2>
          <div className="text-gray-400 leading-relaxed font-light space-y-3">
            {children}
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Header Component
const Header = () => {
  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#0a0a1a]/90 border-b border-purple-900/30">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <a href="/astrodots" className="flex items-center gap-3" data-testid="astrodots-logo">
            <img src="/astrodots-logo.png" alt="AstroDots" className="h-10 w-10 object-contain" />
            <span className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-white">
              Astro<span className="text-purple-400">Dots</span>
            </span>
          </a>
          <Button 
            asChild
            variant="ghost"
            className="text-gray-300 hover:text-white hover:bg-purple-900/30"
            data-testid="back-btn"
          >
            <a href="/astrodots">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to AstroDots
            </a>
          </Button>
        </div>
      </div>
    </header>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="py-12 bg-[#0a0a1a] border-t border-purple-900/30">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="flex flex-col items-center justify-center text-center">
          <div className="flex items-center gap-3 mb-4">
            <img src="/astrodots-logo.png" alt="AstroDots" className="h-8 w-8 object-contain" />
            <span className="font-heading text-lg font-bold text-white">
              Astro<span className="text-purple-400">Dots</span>
            </span>
          </div>
          <p className="text-sm text-gray-500">
            &copy; 2026 LabrHood Labs
          </p>
        </div>
      </div>
    </footer>
  );
};

export default function AstroDotsTerms() {
  const lastUpdated = "January 28, 2026";

  return (
    <div className="min-h-screen bg-[#0a0a1a]">
      <Header />
      
      <main className="pt-32 pb-24">
        <div className="max-w-4xl mx-auto px-6 sm:px-12">
          {/* Hero */}
          <AnimatedSection className="mb-16">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-purple-400 mb-4 block">
              Legal
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white mb-6" data-testid="terms-headline">
              Terms of Service
            </h1>
            <p className="text-lg text-gray-400 font-light mb-4">
              Please read these Terms of Service carefully before using the AstroDots application.
            </p>
            <p className="text-sm text-gray-500">
              Last Updated: {lastUpdated}
            </p>
          </AnimatedSection>

          {/* Introduction */}
          <AnimatedSection className="mb-12">
            <p className="text-gray-400 leading-relaxed font-light">
              By downloading, installing, or using AstroDots ("the App"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the App.
            </p>
          </AnimatedSection>

          <div className="h-px bg-purple-900/30 mb-12" />

          {/* Terms Sections */}
          <TermsSection 
            icon={Star} 
            title="Entertainment Purpose" 
            testId="terms-entertainment"
          >
            <p>
              <strong className="text-purple-300">AstroDots is designed exclusively for entertainment and self-reflection purposes.</strong> All readings, analyses, and insights provided by the App—including those from Iris Odyssey, Celestial Palmistry, and Cosmic Alignment features—are intended for personal amusement and introspection only.
            </p>
            <p>
              The celestial resonance readings, bio-rhythm analyses, and astrological interpretations generated by the App are created using entertainment algorithms and should be enjoyed as a form of digital fortune-telling or horoscope experience.
            </p>
          </TermsSection>

          <TermsSection 
            icon={AlertTriangle} 
            title="No Medical or Professional Advice" 
            testId="terms-no-medical"
          >
            <p>
              <strong className="text-purple-300">AstroDots does NOT provide medical, health, psychological, or any other form of professional advice.</strong>
            </p>
            <p>
              The iris and palm analyses performed by the App are for entertainment only and should NOT be used to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Diagnose or treat any medical condition</li>
              <li>Make health-related decisions</li>
              <li>Replace consultation with qualified healthcare professionals</li>
              <li>Assess or monitor any physical or mental health condition</li>
            </ul>
            <p className="mt-4">
              If you have health concerns, please consult a licensed medical professional.
            </p>
          </TermsSection>

          <TermsSection 
            icon={Scale} 
            title="No Astronomical or Scientific Advice" 
            testId="terms-no-astronomy"
          >
            <p>
              <strong className="text-purple-300">AstroDots does NOT provide accurate astronomical, scientific, or academic information.</strong>
            </p>
            <p>
              The celestial and astrological content in the App is based on traditional astrology and entertainment interpretations, not scientific astronomy. Any references to planets, stars, constellations, or cosmic phenomena are presented in an entertainment context and should not be relied upon for:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Academic or educational purposes</li>
              <li>Scientific research or study</li>
              <li>Navigation or astronomical observation</li>
              <li>Any decision requiring factual astronomical data</li>
            </ul>
          </TermsSection>

          <TermsSection 
            icon={FileText} 
            title="User Responsibilities" 
            testId="terms-responsibilities"
          >
            <p>
              By using AstroDots, you acknowledge and agree that:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>You are at least 18 years of age</li>
              <li>You understand the App is for entertainment purposes only</li>
              <li>You will not rely on the App's content for medical, health, or professional decisions</li>
              <li>You assume full responsibility for how you interpret and use the readings provided</li>
              <li>You will use the camera features only for your own iris and palm analysis</li>
            </ul>
          </TermsSection>

          <TermsSection 
            icon={Shield} 
            title="Limitation of Liability" 
            testId="terms-liability"
          >
            <p>
              LabrHood Labs and AstroDots shall not be liable for any direct, indirect, incidental, consequential, or punitive damages arising from:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Your use of or inability to use the App</li>
              <li>Any decisions made based on the App's entertainment content</li>
              <li>Any interpretation of readings or analyses provided</li>
              <li>Reliance on the App for purposes beyond entertainment</li>
            </ul>
            <p className="mt-4">
              The App is provided "as is" without warranties of any kind, express or implied.
            </p>
          </TermsSection>

          <div className="h-px bg-purple-900/30 mb-12" />

          {/* Agreement Notice */}
          <AnimatedSection>
            <div className="bg-purple-900/20 border border-purple-500/20 p-8 sm:p-12">
              <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-white mb-4">
                Agreement
              </h2>
              <p className="text-gray-400 leading-relaxed font-light mb-6">
                By continuing to use AstroDots, you confirm that you have read, understood, and agree to these Terms of Service and our Privacy Policy.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  asChild
                  className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-6 text-base font-medium"
                  data-testid="privacy-link"
                >
                  <a href="/astrodots/privacy">View Privacy Policy</a>
                </Button>
                <Button 
                  asChild
                  variant="outline"
                  className="border-purple-500 text-purple-400 hover:bg-purple-900/30 px-8 py-6 text-base font-medium"
                  data-testid="support-link"
                >
                  <a href="/astrodots#support">Contact Support</a>
                </Button>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </main>

      <Footer />
    </div>
  );
}
